/**
 * Message attachment, mirroring the mobile app's `Attachment` model.
 * Sent in the chat request body (after base64-encoding the bytes) — the
 * backend's `format_message_for_openai` reads `mimeType` to decide whether
 * to inline as image_url, decode as text, or pass to the document branch.
 */
export interface ChatAttachment {
  fileName: string
  mimeType: string
  /** Base64-encoded bytes, no `data:` prefix. */
  data: string
  /** Local-only display URL (object URL) for image previews — not sent to backend. */
  previewUrl?: string
}

export interface Message {
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  timestamp: number
  isStreaming?: boolean
  toolName?: string // for tool_progress / tool_executed events shown as system messages
  /** Attachments sent with this message (user role only) */
  attachments?: ChatAttachment[]
}

export interface SessionState {
  voiceChatId: string
  discussionId: string
  personaId: string
  storageMode: 'cloud'
}

export interface ChatSession {
  id: string
  discussion_id: string
  persona: string
  session_type: string | null
  started_at: string
  ended_at: string | null
  summary: string | null
  message_count?: number
  messages_updated_at?: string | null
}

export interface CloudMemory {
  user_details: string
  diary: string
  note: string
  future_events: string
}

export interface Persona {
  id: string
  name: string
  emoji: string
}

/** Default personas used as fallback before cloud personas are loaded */
export const DEFAULT_PERSONAS: Persona[] = [
  { id: 'pipar', name: 'PiPar', emoji: '\u{1F43B}\u{200D}\u{2744}\uFE0F}' },
  { id: 'alex', name: 'Alex', emoji: '\u{1F9D1}\u{200D}\u{1F4BB}' },
  { id: 'casey', name: 'Casey', emoji: '\u{1F3A8}' },
  { id: 'river', name: 'River', emoji: '\u{1F30A}' },
  { id: 'basil', name: 'Basil', emoji: '\u{1F33F}' },
  { id: 'custom', name: 'Custom', emoji: '\u{2699}\uFE0F' },
]

/** Well-known persona display info (emoji + display name + role tagline) */
export const PERSONA_INFO: Record<string, { name: string; emoji: string; role: string }> = {
  pipar: { name: 'PiPar', emoji: '\u{1F43B}\u{200D}\u{2744}\uFE0F}', role: 'Minding Your Business' },
  alex: { name: 'Alex', emoji: '\u{1F9D1}\u{200D}\u{1F4BB}', role: 'The Assistant in Expertise' },
  casey: { name: 'Casey', emoji: '\u{1F3A8}', role: 'Health & Wellness' },
  river: { name: 'River', emoji: '\u{1F30A}', role: 'The Friend & Listener' },
  basil: { name: 'Basil', emoji: '\u{1F33F}', role: 'All Things About Food' },
  custom: { name: 'Custom', emoji: '\u{2699}\uFE0F', role: 'Your Custom Persona' },
}

/** @deprecated Use DEFAULT_PERSONAS */
export const PERSONAS = DEFAULT_PERSONAS

export interface ChatEvent {
  type: 'text' | 'content' | 'complete' | 'error' | 'tool_call' | 'tool_executed' | 'tool_progress' | 'cloud_tool_result' | 'generated_content' | 'email_review_required' | 'metadata' | 'guard_active' | 'model_access'
  data?: string | Record<string, unknown>
  cost_info?: Record<string, unknown>
  messages_remaining?: number
  server_tags?: Record<string, unknown>
  /** `guard_active` and `model_access` are flat frames, not `data`-wrapped:
   *  the backend puts their fields at the top level so a client can switch on
   *  `type` and read them directly. See GuardActiveEvent / ModelAccessEvent. */
  error?: string
  guard?: 'window' | 'weekly' | string
  used?: number
  limit?: number
  resets_in_seconds?: number
  continue_on_credits?: {
    available?: boolean
    credits_per_message_estimate?: number
    credits_left?: number
  }
  model?: string
  tier?: string
  fallback_model?: string
  upgrade_tiers?: string[]
}

/**
 * A free-model allowance ran out (backend `services/zero_rating_guards.py`).
 *
 * Deliberately NOT an error: the user has almost certainly got credits and
 * simply reached the free window, so the next turn is metered rather than
 * refused. The backend emits this frame and then a plain `error` frame for
 * clients that do not know the type — we handle the first and stop, so the
 * fallback never reaches the UI.
 */
export interface GuardActiveEvent {
  guard: 'window' | 'weekly' | string
  used?: number
  limit?: number
  resets_in_seconds?: number
  continue_on_credits?: {
    available?: boolean
    credits_per_message_estimate?: number
    credits_left?: number
  }
  error?: string
}

/**
 * The requested model is not in the user's subscription tier (backend
 * `services/model_access.py`). Carries the model to fall back to so the client
 * can re-point the persona rather than leaving the user on a model that fails
 * on every message. Same two-frame pattern as GuardActiveEvent.
 */
export interface ModelAccessEvent {
  model?: string
  tier?: string
  fallback_model?: string
  upgrade_tiers?: string[]
  error?: string
}

/** Emitted when the AI's send_message tool produces an outbound email to an
 * external (non-PiPar) recipient. The web client must show an approval dialog
 * and, on user approval, POST to /contacts/send_message itself. */
export interface EmailReviewRequest {
  to_email: string
  recipient_display_name?: string
  subject: string
  body: string
  sender_pipar_email: string
  sender_display_name?: string
  is_contact: boolean
  contact_id?: string
}

/** Emitted when backend executes a cloud tool that modifies data */
export interface CloudToolResult {
  tool: string
  action: 'created' | 'updated' | 'completed' | 'deleted'
  entity_type: 'task' | 'note' | 'follow_up' | 'message' | 'bulletin'
  entity_id: string
  entity_data: Record<string, unknown> | null
}

/** Emitted when backend generates a document or image for user preview */
export interface GeneratedContent {
  content_type: 'document' | 'image'
  note_id: string
  // Document fields
  subject?: string
  summary?: string
  format?: string        // 'html', 'md', 'text'
  mime_type?: string
  display_format?: string // 'HTML', 'Markdown', 'Text'
  is_app?: boolean
  app_name?: string
  emoji?: string
  document_type?: string
  blob_key?: string
  content_preview?: string  // First ~3000 chars of content
  // Image fields
  image_name?: string
  prompt?: string
  revised_prompt?: string
  aspect_ratio?: string
  model?: string
}

export interface TestResult {
  name: string
  passed: boolean
  details: string
  duration: number
  costInfo?: Record<string, unknown>
}

// ── Cloud Data Sync Types ──────────────────────────────────────

export interface CloudTask {
  id: string
  title: string
  description?: string
  // Flutter uses camelCase in toJson()
  dueDate?: string
  reminderTime?: string
  scheduledTime?: string
  duration?: number          // in minutes
  priority?: number          // 0=low, 1=medium, 2=high, 3=critical
  effort?: number            // 0=none, 1=low, 2=medium, 3=high
  status?: number            // 0=notStarted, 1=inProgress, 2=completed, 3=cancelled, 4=todo, 5=done
  isAllDay?: boolean
  location?: string
  type?: number              // 0=task, 1=event
  personaId?: string
  tags?: string[]
  subTasks?: Array<{ id: string; title: string; isCompleted: boolean; createdAt?: string }>
  projectId?: string
  emoji?: string
  // Parallel arrays mirroring Flutter's Task model: imagePaths is local-only
  // (mobile filesystem paths), imageBlobKeys are the R2 blob keys for cloud
  // sync (empty string = not uploaded yet), imageThumbnailKeys mirrors that
  // for thumbnails.
  imagePaths?: string[]
  imageBlobKeys?: string[]
  imageThumbnailKeys?: string[]
  createdAt?: string
  lastUpdatedAt?: string
  completedAt?: string
  recurrence?: Record<string, unknown>
  _cloud_updated_at?: string
  _cloud_deleted?: boolean
  [key: string]: unknown
}

export interface CloudProject {
  id: string
  name: string
  description?: string
  color_index?: number       // from Project.toMap() which uses snake_case
  created_at?: string
  updated_at?: string
  is_completed?: number | boolean
  _cloud_updated_at?: string
  _cloud_deleted?: boolean
}

export interface CloudNote {
  id: string
  title?: string
  content?: string
  type?: number            // NoteType index
  tags?: string[]
  attachments?: Array<{
    id: string
    file_path?: string
    filePath?: string       // camelCase variant from Flutter
    blob_key?: string       // Cloud blob storage key (R2)
    blobKey?: string        // camelCase variant from Flutter
    type?: number
    description?: string
    metadata?: Record<string, unknown>
    [key: string]: unknown
  }>
  transcription?: string
  is_ai_generated_document?: boolean
  is_ai_generated_image?: boolean
  is_generated_pdf?: boolean
  is_app?: boolean
  created_at?: string
  last_updated_at?: string
  metadata?: Record<string, unknown>
  _cloud_updated_at?: string
  _cloud_deleted?: boolean
  [key: string]: unknown
}

export interface CloudEmail {
  id: string
  direction: 'inbound' | 'sent'
  // Inbound fields
  mail_id?: string
  sender?: string
  from_field?: string
  recipient?: string
  subject?: string
  body_plain?: string
  body_html?: string
  priority?: string
  is_read?: number
  received_at?: string
  // Sent fields
  from_email?: string
  to_email?: string
  to_name?: string
  body?: string
  sent_at?: string
  created_at?: string
  _cloud_updated_at?: string
}

/**
 * Flutter uploads camelCase keys (see pipar/lib/models/user_contact.dart `toJson`).
 * snake_case variants are kept as fallbacks for any legacy data.
 */
export interface CloudContact {
  id: string
  // Flutter camelCase (authoritative)
  friendlyName?: string
  fullName?: string
  externalEmail?: string
  piparEmail?: string
  isPiparReceiver?: boolean
  isImportantSender?: boolean
  isBlocked?: boolean
  invitationStatus?: string
  createdAt?: string
  updatedAt?: string
  imageBlobKey?: string
  imageThumbnailKey?: string
  // Legacy snake_case fallbacks
  friendly_name?: string
  full_name?: string
  external_email?: string
  pipar_email?: string
  is_pipar_receiver?: boolean
  is_important_sender?: boolean
  is_blocked?: boolean
  invitation_status?: string
  created_at?: string
  updated_at?: string
  image_blob_key?: string
  image_thumbnail_key?: string
  _cloud_updated_at?: string
  _cloud_deleted?: boolean
  [key: string]: unknown
}

/**
 * Flutter uploads camelCase keys (see pipar/lib/models/user_location.dart `toJson`).
 */
export interface CloudLocation {
  id: string
  title: string
  address?: string
  latitude?: number
  longitude?: number
  // Flutter camelCase
  imagePath?: string
  createdAt?: string
  updatedAt?: string
  // Legacy snake_case fallbacks
  created_at?: string
  updated_at?: string
  // Image blob keys (added by Flutter cloud_sync_service)
  // New (camelCase) preferred; snake_case kept for legacy cloud rows.
  imageBlobKey?: string
  imageThumbnailKey?: string
  image_blob_key?: string
  image_thumbnail_key?: string
  _cloud_updated_at?: string
  _cloud_deleted?: boolean
  [key: string]: unknown
}

/** NoteType index to label mapping */
export const NOTE_TYPE_LABELS: Record<number, string> = {
  0: 'Text',
  1: 'Voice',
  2: 'Image',
  3: 'Mixed',
  4: 'Follow-up',
  5: 'Alert',
  6: 'Postpone Reminder',
  7: 'Bulletin',
  8: 'Box',
  9: 'Proactive Reminder',
  10: 'Morning Briefing',
}

/** Task status index to label mapping */
export const TASK_STATUS_LABELS: Record<number, string> = {
  0: 'Not Started',
  1: 'In Progress',
  2: 'Completed',
  3: 'Cancelled',
  4: 'Todo',
  5: 'Done',
}

export const TASK_PRIORITY_LABELS: Record<number, string> = {
  0: 'Low',
  1: 'Medium',
  2: 'High',
  3: 'Critical',
}

export const TASK_PRIORITY_COLORS: Record<number, string> = {
  0: '#4caf50',
  1: '#ff9800',
  2: '#f44336',
  3: '#9c27b0',
}

export const TASK_TYPE_LABELS: Record<number, string> = {
  0: 'Task',
  1: 'Event',
}
